import { ReactNode, createContext, useEffect, useState } from "react";
import {
  TileApiResponse,
  fromApiTimeFormat,
  useLoopFrames,
} from "../shared/tileOverlay";

import { TileOverlayContextState } from "./TileOverlayContext";
import config from "../config";

export type WrfProduct = "rainfall" | "wind";

const OTHER: Record<WrfProduct, WrfProduct> = {
  rainfall: "wind",
  wind: "rainfall",
};

interface WrfApiProducts {
  rainfall?: TileApiResponse;
  wind?: TileApiResponse;
}

export interface WrfContextState extends TileOverlayContextState {
  rainfallApiResponse?: TileApiResponse;
  windApiResponse?: TileApiResponse;
  activeApiResponse?: TileApiResponse;
  product: WrfProduct;
  setProduct: (product: WrfProduct) => void;
  // NOTE: setFramesLoaded is intentionally NOT exposed — per TileOverlayContext,
  // it's an internal setter. framesLoaded is driven by the provider's own effect.
}

const WrfContext = createContext<WrfContextState | undefined>(undefined);

export const WrfContextProvider = ({ children }: { children: ReactNode }) => {
  const [frame, setFrame] = useState<number>(0);
  const [speed, setSpeed] = useState<number>(500);
  const [playing, setPlaying] = useState<boolean>(false);
  const [visible, setVisible] = useState<boolean>(false);
  const [opacity, setOpacity] = useState<number>(0.5);
  const [product, setProductState] = useState<WrfProduct>("rainfall");
  const [apiProducts, setApiProducts] = useState<WrfApiProducts>({});
  const [framesLoaded, setFramesLoaded] = useState<number>(0);

  const forecastEnabled = config.forecast === true;

  const [apiUrl, setApiUrl] = useState<string>(
    process.env.REACT_APP_API_URL || "https://jefdaq.jefulleralert.com"
  );

  // No-op fetch when forecast is disabled for this deployment.
  useEffect(() => {
    if (!forecastEnabled) return;
    let warned = false;
    const update = () =>
      fetchWrfAPI(apiUrl)
        .then(setApiProducts)
        .catch((e: any) => {
          if (!apiUrl.includes("jefulleralert")) {
            setApiUrl("https://jefdaq.jefulleralert.com");
          } else if (!warned) {
            // Steady state until jefdaq serves WRF: /json/radar/ returns only
            // D0Q, so this throws every tick. Warn once to avoid log spam.
            warned = true;
            console.warn("WRF forecast data not available:", e?.message ?? e);
          }
        });
    update();
    const interval = setInterval(update, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, [forecastEnabled, apiUrl]);

  const rainfallApiResponse = apiProducts.rainfall;
  const windApiResponse = apiProducts.wind;

  // Auto-select an available product: if the selected product has no data but
  // the other does, switch to it (covers initial load and data dropping on refetch).
  useEffect(() => {
    if (apiProducts[product]) return;
    const other = OTHER[product];
    if (apiProducts[other]) setProductState(other);
  }, [product, apiProducts]);

  const activeApiResponse =
    product === "rainfall" ? rainfallApiResponse : windApiResponse;
  const framesTotal = activeApiResponse?.times.length ?? 0;

  // Keep frame within the active product's range across any switch/refetch.
  useEffect(() => {
    if (framesTotal > 0 && frame >= framesTotal) setFrame(0);
  }, [framesTotal, frame]);

  // WRF tiles (like legacy radar) load all at once, so "frames loaded" tracks
  // the active product's frame count. Drive it from the data HERE rather than
  // from WrfLayer: the layer only mounts while visible, so a layer-driven
  // counter would strand the panel (spinner hides Play/Stop) if framesTotal
  // grew after the user pressed Stop. Context-driven keeps it always in sync.
  useEffect(() => {
    setFramesLoaded(framesTotal);
  }, [framesTotal]);

  const setProduct = (newProduct: WrfProduct) => {
    // Immediate clamp on user action; the [framesTotal, frame] effect above is
    // the authoritative guard (it also covers the auto-switch/refetch paths).
    const newResponse =
      newProduct === "rainfall" ? rainfallApiResponse : windApiResponse;
    const newFrameCount = newResponse?.times.length ?? 0;
    if (frame >= newFrameCount) setFrame(0);
    setProductState(newProduct);
  };

  useLoopFrames(playing ? speed : 0, frame, !visible, framesTotal, setFrame);

  const state: WrfContextState = {
    rainfallApiResponse,
    windApiResponse,
    activeApiResponse,
    product,
    setProduct,
    frame,
    playing,
    setPlaying,
    visible,
    setVisible,
    speed,
    setSpeed,
    opacity,
    setOpacity,
    framesLoaded,
    framesTotal,
  };
  return <WrfContext.Provider value={state}>{children}</WrfContext.Provider>;
};

const parseTileProduct = (data: any): TileApiResponse | undefined => {
  if (!data?.times || data.times.length === 0) return undefined;
  // baseURL is required to build tile URLs; defend against a malformed product.
  if (!data.baseURL) return undefined;
  const times = data.times.map(fromApiTimeFormat);
  // The API emits the key as `nulltile` (lowercase t, see jefdaq radar.py);
  // normalize to nullTile so WrfLayer's errorTileUrl resolves. It is only a
  // non-essential error-tile fallback (the overlay renders without it), so we
  // intentionally do NOT reject a product that lacks it.
  const nullTile = data.nullTile ?? data.nulltile;
  return { ...data, nullTile, times };
};

const fetchWrfAPI = (apiUrl: string): Promise<WrfApiProducts> => {
  return fetch(apiUrl + "/json/radar/")
    .then((res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    })
    .then((result) => {
      const rainfall = parseTileProduct(result["WRF"]);
      const wind = parseTileProduct(result["WRF_Wind"]);
      if (!rainfall && !wind) throw new Error("No WRF data available");
      return { rainfall, wind };
    });
};

export default WrfContext;
